import { Game, SoccerCompetition, SportType, Team } from '@/types';

type TeamColor = {
  primaryColor: string;
  secondaryColor: string;
};

type TeamColorOverride = TeamColor & {
  ids: string[];
  names: string[];
};

const UPSTREAM_PLACEHOLDER_COLORS = new Set(['#808080']);

const soccerTeamColorOverrides: TeamColorOverride[] = [
  { ids: ['328'], names: ['Burnley FC'], primaryColor: '#6C1D45', secondaryColor: '#99D6EA' },
  { ids: ['341'], names: ['Leeds United FC'], primaryColor: '#FFFFFF', secondaryColor: '#1D428A' },
  { ids: ['71'], names: ['Sunderland AFC'], primaryColor: '#EB172B', secondaryColor: '#FFFFFF' },
  { ids: ['457'], names: ['US Cremonese'], primaryColor: '#D71920', secondaryColor: '#8A8C8F' },
  { ids: ['471'], names: ['US Sassuolo Calcio'], primaryColor: '#00A651', secondaryColor: '#000000' },
  { ids: ['7'], names: ['Hamburger SV'], primaryColor: '#005CA9', secondaryColor: '#FFFFFF' },
  { ids: ['525'], names: ['FC Lorient'], primaryColor: '#FF6B00', secondaryColor: '#000000' },
  { ids: ['285'], names: ['Elche CF'], primaryColor: '#FFFFFF', secondaryColor: '#00843D' },
  { ids: ['88'], names: ['Levante UD'], primaryColor: '#B6002A', secondaryColor: '#003A70' },
  { ids: ['1048'], names: ['Real Oviedo'], primaryColor: '#005BAC', secondaryColor: '#FFFFFF' },
  { ids: ['1049'], names: ['CD Tondela'], primaryColor: '#00843D', secondaryColor: '#FFCD00' },
  { ids: ['7822'], names: ['FC Alverca'], primaryColor: '#E30613', secondaryColor: '#FFFFFF' },
  { ids: ['4520'], names: ['Barcelona SC'], primaryColor: '#FFD100', secondaryColor: '#000000' },
  { ids: ['2061'], names: ['CA Boca Juniors'], primaryColor: '#0033A0', secondaryColor: '#FFD100' },
  { ids: ['2066'], names: ['CA Lanus'], primaryColor: '#7A003C', secondaryColor: '#FFFFFF' },
  { ids: ['5184'], names: ['CA Penarol'], primaryColor: '#FFD100', secondaryColor: '#000000' },
  { ids: ['7580'], names: ['CA Platense'], primaryColor: '#6B3F2A', secondaryColor: '#FFFFFF' },
  { ids: ['2070'], names: ['CA Rosario Central'], primaryColor: '#0055A4', secondaryColor: '#FFD100' },
  { ids: ['6989'], names: ['CAR Independiente del Valle'], primaryColor: '#000000', secondaryColor: '#005EB8' },
  { ids: ['7912'], names: ['CD Coquimbo Unido'], primaryColor: '#F7D117', secondaryColor: '#000000' },
  { ids: ['7119'], names: ['CD Independiente Medellin'], primaryColor: '#D71920', secondaryColor: '#003A70' },
  { ids: ['4437'], names: ['CD Tolima'], primaryColor: '#7A1E2B', secondaryColor: '#D6B04C' },
  { ids: ['6993'], names: ['CD Universidad Catolica'], primaryColor: '#00A3E0', secondaryColor: '#FFFFFF' },
  { ids: ['4439'], names: ['CDP Junior FC'], primaryColor: '#D71920', secondaryColor: '#FFFFFF' },
  { ids: ['7865'], names: ['Club Always Ready'], primaryColor: '#D71920', secondaryColor: '#FFFFFF' },
  { ids: ['4261'], names: ['Club Bolivar'], primaryColor: '#6EC6F3', secondaryColor: '#FFFFFF' },
  { ids: ['9373'], names: ['Club Cerro Porteno'], primaryColor: '#003DA5', secondaryColor: '#D50032' },
  { ids: ['9379'], names: ['Club Libertad Asuncion'], primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  { ids: ['7055'], names: ['Club Nacional de Football'], primaryColor: '#FFFFFF', secondaryColor: '#0038A8' },
  { ids: ['4268'], names: ['Club Universitario de Deportes'], primaryColor: '#F5E6C8', secondaryColor: '#7A0C18' },
  { ids: ['1783'], names: ['CR Flamengo'], primaryColor: '#E31B23', secondaryColor: '#000000' },
  { ids: ['1771'], names: ['Cruzeiro EC'], primaryColor: '#003DA5', secondaryColor: '#FFFFFF' },
  { ids: ['5347'], names: ['CS Cristal'], primaryColor: '#4FC3F7', secondaryColor: '#FFFFFF' },
  { ids: ['2052'], names: ['CS Independiente Rivadavia'], primaryColor: '#003DA5', secondaryColor: '#FFFFFF' },
  { ids: ['5683'], names: ['Cusco FC'], primaryColor: '#F2C94C', secondaryColor: '#000000' },
  { ids: ['5170'], names: ['Deportivo La Guaira FC'], primaryColor: '#F47B20', secondaryColor: '#000000' },
  { ids: ['2051'], names: ['Estudiantes de La Plata'], primaryColor: '#ED1C24', secondaryColor: '#FFFFFF' },
  { ids: ['1765'], names: ['Fluminense FC'], primaryColor: '#8A1538', secondaryColor: '#006837' },
  { ids: ['4441'], names: ['Independiente Santa Fe'], primaryColor: '#E30613', secondaryColor: '#FFFFFF' },
  { ids: ['4528'], names: ['LDU de Quito'], primaryColor: '#FFFFFF', secondaryColor: '#D71920' },
  { ids: ['4364'], names: ['Mirassol FC'], primaryColor: '#FFD100', secondaryColor: '#009739' },
  { ids: ['1779'], names: ['SC Corinthians Paulista'], primaryColor: '#000000', secondaryColor: '#FFFFFF' },
  { ids: ['1769'], names: ['SE Palmeiras'], primaryColor: '#006437', secondaryColor: '#FFFFFF' },
  { ids: ['9357'], names: ['Universidad Central de Venezuela FC'], primaryColor: '#B00020', secondaryColor: '#003DA5' }
];

const normalizeTeamKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const soccerTeamColorsById = new Map<string, TeamColor>();
const soccerTeamColorsByName = new Map<string, TeamColor>();

for (const override of soccerTeamColorOverrides) {
  for (const id of override.ids) {
    soccerTeamColorsById.set(id, override);
  }

  for (const name of override.names) {
    soccerTeamColorsByName.set(normalizeTeamKey(name), override);
  }
}

const isPlaceholderColor = (color?: string): boolean =>
  !color || UPSTREAM_PLACEHOLDER_COLORS.has(color.toUpperCase());

const getSoccerTeamColorOverride = (team: Team): TeamColor | undefined =>
  soccerTeamColorsById.get(team.id) ||
  soccerTeamColorsByName.get(normalizeTeamKey(team.name));

export const resolveTeamColors = (
  team: Team,
  sportType: SportType,
  _competition?: SoccerCompetition
): Team => {
  if (sportType !== 'soccer') {
    return team;
  }

  const override = getSoccerTeamColorOverride(team);

  return {
    ...team,
    primaryColor: isPlaceholderColor(team.primaryColor)
      ? override?.primaryColor
      : team.primaryColor,
    secondaryColor: isPlaceholderColor(team.secondaryColor)
      ? override?.secondaryColor || '#FFFFFF'
      : team.secondaryColor
  };
};

export const resolveGameTeamColors = <T extends Pick<Game, 'homeTeam' | 'awayTeam' | 'sportType' | 'competition'>>(
  game: T
): T => ({
  ...game,
  homeTeam: resolveTeamColors(game.homeTeam, game.sportType, game.competition),
  awayTeam: resolveTeamColors(game.awayTeam, game.sportType, game.competition)
});
